export const SCENARIO_INVESTMENT_REQUIREMENTS_SCHEMA_VERSION =
  'scenario_investment_requirements_v1' as const;

export const SCENARIO_INVESTMENT_REQUIREMENT_KINDS = [
  'human_capital',
  'technology',
  'one_time_cost',
  'working_capital',
] as const;

export const SCENARIO_INVESTMENT_COST_BASIS_RUNGS = [
  'own_comparable',
  'industry_benchmark',
  'named_gap',
] as const;

export const SCENARIO_INVESTMENT_REQUIREMENT_QUANTITY_UNITS = [
  'headcount',
  'fte',
  'license',
  'seat',
  'usd',
  'hours',
  'days',
  'other',
] as const;

export const SCENARIO_INVESTMENT_REQUIREMENT_TIMING_CADENCES = [
  'one_time',
  'monthly',
  'quarterly',
  'annual',
  'ongoing',
] as const;

export const SCENARIO_INVESTMENT_REQUIREMENT_BASIS_TYPES = [
  'evidence',
  'formula',
  'evidence_and_formula',
  'named_gap',
] as const;

export const SCENARIO_INVESTMENT_CAPABILITY_CHECK_STATUSES = [
  'have',
  'partial',
  'gap',
] as const;

export type ScenarioInvestmentRequirementsSchemaVersion =
  typeof SCENARIO_INVESTMENT_REQUIREMENTS_SCHEMA_VERSION;

export type ScenarioInvestmentRequirementKind =
  (typeof SCENARIO_INVESTMENT_REQUIREMENT_KINDS)[number];

export type ScenarioInvestmentCostBasisRung =
  (typeof SCENARIO_INVESTMENT_COST_BASIS_RUNGS)[number];

export type ScenarioInvestmentRequirementQuantityUnit =
  (typeof SCENARIO_INVESTMENT_REQUIREMENT_QUANTITY_UNITS)[number];

export type ScenarioInvestmentRequirementTimingCadence =
  (typeof SCENARIO_INVESTMENT_REQUIREMENT_TIMING_CADENCES)[number];

export type ScenarioInvestmentRequirementBasisType =
  (typeof SCENARIO_INVESTMENT_REQUIREMENT_BASIS_TYPES)[number];

export type ScenarioInvestmentCapabilityCheckStatus =
  (typeof SCENARIO_INVESTMENT_CAPABILITY_CHECK_STATUSES)[number];

export type ScenarioInvestmentRequirementQuantity = Readonly<{
  amount: number;
  unit: ScenarioInvestmentRequirementQuantityUnit;
  label?: string | null;
}>;

export type ScenarioInvestmentRequirementRole = Readonly<{
  title: string;
  roleId?: string | null;
  capabilityRef?: string | null;
}>;

export type ScenarioInvestmentCostUsdBinding = Readonly<{
  amountUsd: number;
  currency: 'USD';
  bindingRef: string;
  evidenceRef?: string | null;
  formulaRef?: string | null;
  asOf: string;
}>;

export type ScenarioInvestmentNamedCostGap = Readonly<{
  gapId: string;
  label: string;
  requiredEvidence: string;
  ownerHint?: string | null;
}>;

export type ScenarioInvestmentRequirementBasis = Readonly<{
  basisType: ScenarioInvestmentRequirementBasisType;
  basisRef: string;
  description: string;
  sourceClass?: ScenarioInvestmentCostBasisRung | null;
}>;

export type ScenarioInvestmentRequirementTiming = Readonly<{
  window: string;
  cadence?: ScenarioInvestmentRequirementTimingCadence | null;
  startDate?: string | null;
  endDate?: string | null;
}>;

export type ScenarioInvestmentRequirementCapabilityCheck = Readonly<{
  status: ScenarioInvestmentCapabilityCheckStatus;
  requiredCount: number;
  availableCount: number;
  gapCount: number;
  unit: 'fte' | 'headcount';
  sourceRef: string;
  description: string;
}>;

export type ScenarioInvestmentRequirementV1 = Readonly<{
  requirementId: string;
  kind: ScenarioInvestmentRequirementKind;
  description: string;
  quantity?: ScenarioInvestmentRequirementQuantity | null;
  role?: ScenarioInvestmentRequirementRole | null;
  costUsd?: ScenarioInvestmentCostUsdBinding | null;
  namedCostGap?: ScenarioInvestmentNamedCostGap | null;
  basis: ScenarioInvestmentRequirementBasis;
  costBasisRung: ScenarioInvestmentCostBasisRung;
  timing: ScenarioInvestmentRequirementTiming;
  capabilityCheck?: ScenarioInvestmentRequirementCapabilityCheck | null;
}>;

export type ScenarioInvestmentRequirementsV1 = Readonly<{
  schemaVersion: ScenarioInvestmentRequirementsSchemaVersion;
  scenarioId: string;
  moduleId: string;
  asOf: string;
  requirements: readonly ScenarioInvestmentRequirementV1[];
}>;

export type ScenarioInvestmentRequirementsParseResult =
  | Readonly<{ success: true; data: ScenarioInvestmentRequirementsV1 }>
  | Readonly<{ success: false; error: Error }>;

export const ScenarioInvestmentRequirementsV1Schema = {
  parse(input: unknown): ScenarioInvestmentRequirementsV1 {
    return parseScenarioInvestmentRequirementsV1(input);
  },
  safeParse(input: unknown): ScenarioInvestmentRequirementsParseResult {
    try {
      return { success: true, data: parseScenarioInvestmentRequirementsV1(input) };
    } catch (error: unknown) {
      return {
        success: false,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  },
};

export function parseScenarioInvestmentRequirementsV1(
  input: unknown,
): ScenarioInvestmentRequirementsV1 {
  const record = requireRecord(input, 'ScenarioInvestmentRequirementsV1');
  const schemaVersion = requireString(record.schemaVersion, 'schemaVersion');
  if (schemaVersion !== SCENARIO_INVESTMENT_REQUIREMENTS_SCHEMA_VERSION) {
    throw new Error(
      `Invalid ScenarioInvestmentRequirementsV1 schemaVersion '${schemaVersion}'.`,
    );
  }

  requireString(record.scenarioId, 'scenarioId');
  requireString(record.moduleId, 'moduleId');
  requireString(record.asOf, 'asOf');

  const requirements = requireArray(record.requirements, 'requirements');
  if (requirements.length === 0) {
    throw new Error('ScenarioInvestmentRequirementsV1 requirements must not be empty.');
  }

  requirements.forEach((requirement, index) => {
    validateRequirement(requirement, `requirements[${index}]`);
  });

  return input as ScenarioInvestmentRequirementsV1;
}

function validateRequirement(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  requireString(record.requirementId, `${path}.requirementId`);
  requireEnum(record.kind, SCENARIO_INVESTMENT_REQUIREMENT_KINDS, `${path}.kind`);
  requireString(record.description, `${path}.description`);

  validateNullableQuantity(record.quantity, `${path}.quantity`);
  validateNullableRole(record.role, `${path}.role`);

  const hasCostUsd = record.costUsd !== undefined && record.costUsd !== null;
  const hasNamedGap = record.namedCostGap !== undefined && record.namedCostGap !== null;
  if (hasCostUsd === hasNamedGap) {
    throw new Error(`${path} must carry exactly one of costUsd or namedCostGap.`);
  }

  const rung = requireEnum(
    record.costBasisRung,
    SCENARIO_INVESTMENT_COST_BASIS_RUNGS,
    `${path}.costBasisRung`,
  );

  if (rung === 'named_gap') {
    if (!hasNamedGap || hasCostUsd) {
      throw new Error(`${path} named_gap requirements must carry namedCostGap.`);
    }
  } else if (!hasCostUsd || hasNamedGap) {
    throw new Error(`${path} ${rung} requirements must carry governed costUsd.`);
  }

  if (hasCostUsd) {
    validateCostUsdBinding(record.costUsd, `${path}.costUsd`);
  }
  if (hasNamedGap) {
    validateNamedCostGap(record.namedCostGap, `${path}.namedCostGap`);
  }

  validateBasis(record.basis, `${path}.basis`, rung);
  validateTiming(record.timing, `${path}.timing`);
  validateNullableCapabilityCheck(record.capabilityCheck, `${path}.capabilityCheck`);
}

function validateNullableQuantity(value: unknown, path: string): void {
  if (value === undefined || value === null) return;
  const record = requireRecord(value, path);
  requireFiniteNumber(record.amount, `${path}.amount`, { min: 0, exclusiveMin: true });
  requireEnum(record.unit, SCENARIO_INVESTMENT_REQUIREMENT_QUANTITY_UNITS, `${path}.unit`);
  requireOptionalString(record.label, `${path}.label`);
}

function validateNullableRole(value: unknown, path: string): void {
  if (value === undefined || value === null) return;
  const record = requireRecord(value, path);
  requireString(record.title, `${path}.title`);
  requireOptionalString(record.roleId, `${path}.roleId`);
  requireOptionalString(record.capabilityRef, `${path}.capabilityRef`);
}

function validateCostUsdBinding(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  requireFiniteNumber(record.amountUsd, `${path}.amountUsd`, { min: 0 });
  const currency = requireString(record.currency, `${path}.currency`);
  if (currency !== 'USD') {
    throw new Error(`${path}.currency must be USD.`);
  }
  requireString(record.bindingRef, `${path}.bindingRef`);
  const evidenceRef = requireOptionalString(record.evidenceRef, `${path}.evidenceRef`);
  const formulaRef = requireOptionalString(record.formulaRef, `${path}.formulaRef`);
  if (!evidenceRef && !formulaRef) {
    throw new Error(`${path} requires evidenceRef or formulaRef.`);
  }
  requireString(record.asOf, `${path}.asOf`);
}

function validateNamedCostGap(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  requireString(record.gapId, `${path}.gapId`);
  requireString(record.label, `${path}.label`);
  requireString(record.requiredEvidence, `${path}.requiredEvidence`);
  requireOptionalString(record.ownerHint, `${path}.ownerHint`);
}

function validateBasis(
  value: unknown,
  path: string,
  rung: ScenarioInvestmentCostBasisRung,
): void {
  const record = requireRecord(value, path);
  const basisType = requireEnum(
    record.basisType,
    SCENARIO_INVESTMENT_REQUIREMENT_BASIS_TYPES,
    `${path}.basisType`,
  );
  requireString(record.basisRef, `${path}.basisRef`);
  requireString(record.description, `${path}.description`);
  if (record.sourceClass !== undefined && record.sourceClass !== null) {
    requireEnum(record.sourceClass, SCENARIO_INVESTMENT_COST_BASIS_RUNGS, `${path}.sourceClass`);
  }

  if (rung === 'named_gap' && basisType !== 'named_gap') {
    throw new Error(`${path}.basisType must be named_gap for named_gap requirements.`);
  }
  if (rung !== 'named_gap' && basisType === 'named_gap') {
    throw new Error(`${path}.basisType cannot be named_gap for governed cost requirements.`);
  }
}

function validateTiming(value: unknown, path: string): void {
  const record = requireRecord(value, path);
  requireString(record.window, `${path}.window`);
  if (record.cadence !== undefined && record.cadence !== null) {
    requireEnum(
      record.cadence,
      SCENARIO_INVESTMENT_REQUIREMENT_TIMING_CADENCES,
      `${path}.cadence`,
    );
  }
  requireOptionalString(record.startDate, `${path}.startDate`);
  requireOptionalString(record.endDate, `${path}.endDate`);
}

function validateNullableCapabilityCheck(value: unknown, path: string): void {
  if (value === undefined || value === null) return;
  const record = requireRecord(value, path);
  requireEnum(record.status, SCENARIO_INVESTMENT_CAPABILITY_CHECK_STATUSES, `${path}.status`);
  const requiredCount = requireFiniteNumber(record.requiredCount, `${path}.requiredCount`, {
    min: 0,
  });
  const availableCount = requireFiniteNumber(record.availableCount, `${path}.availableCount`, {
    min: 0,
  });
  const gapCount = requireFiniteNumber(record.gapCount, `${path}.gapCount`, { min: 0 });
  requireEnum(record.unit, ['fte', 'headcount'] as const, `${path}.unit`);
  requireString(record.sourceRef, `${path}.sourceRef`);
  requireString(record.description, `${path}.description`);

  const expectedGap = Math.max(0, requiredCount - availableCount);
  if (Math.abs(expectedGap - gapCount) > 0.000001) {
    throw new Error(`${path}.gapCount must equal max(requiredCount - availableCount, 0).`);
  }
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${path} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${path} must be an array.`);
  }
  return value;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`${path} is required.`);
  }
  return value;
}

function requireOptionalString(value: unknown, path: string): string | null | undefined {
  if (value === undefined || value === null) return value;
  return requireString(value, path);
}

function requireFiniteNumber(
  value: unknown,
  path: string,
  opts: Readonly<{ min?: number; exclusiveMin?: boolean }> = {},
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${path} must be a finite number.`);
  }
  if (opts.min !== undefined) {
    const valid = opts.exclusiveMin ? value > opts.min : value >= opts.min;
    if (!valid) {
      const comparator = opts.exclusiveMin ? 'greater than' : 'greater than or equal to';
      throw new Error(`${path} must be ${comparator} ${opts.min}.`);
    }
  }
  return value;
}

function requireEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T {
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    throw new Error(`${path} must be one of: ${allowed.join(', ')}.`);
  }
  return value as T;
}
